import { useCallback, useState } from 'react';

import { useAtom } from 'jotai';

import { draftFoldersAtom } from '../../../../store/fileSystem.js';
import {
  MAX_FOLDER_DEPTH,
  type FolderNode,
} from '../../../../types/fileSystem.js';

export const useCreateFolder = ({
  parentId,
  depth,
}: {
  parentId: string;
  depth: number;
}) => {
  const [folders, setFolders] = useAtom(draftFoldersAtom);
  const [isDrafting, setIsDrafting] = useState(false);

  const startDraft = useCallback(() => setIsDrafting(true), []);
  const cancelDraft = useCallback(() => setIsDrafting(false), []);

  const validateName = useCallback(
    (name: string, excludeId?: string) => {
      const trimmed = name.trim();

      if (!trimmed) return false;

      return !folders.some(
        (folder) =>
          folder.parentId === parentId &&
          folder.id !== excludeId &&
          folder.name.toLowerCase() === trimmed.toLowerCase(),
      );
    },
    [folders, parentId],
  );

  const commitDraft = useCallback(
    (name: string) => {
      if (depth >= MAX_FOLDER_DEPTH) return false;
      if (!validateName(name)) return false;

      const folder: FolderNode = {
        kind: 'folder',
        id: crypto.randomUUID(),
        name: name.trim(),
        parentId,
        fileType: 'folder',
      };

      setFolders((prev) => [...prev, folder]);
      setIsDrafting(false);

      return true;
    },
    [validateName, setFolders, parentId, depth],
  );

  const renameFolder = useCallback(
    (id: string, name: string) => {
      if (!validateName(name, id)) return false;

      setFolders((prev) =>
        prev.map((folder) =>
          folder.id === id ? { ...folder, name: name.trim() } : folder,
        ),
      );

      return true;
    },
    [validateName, setFolders],
  );

  return {
    isDrafting,
    startDraft,
    cancelDraft,
    commitDraft,
    renameFolder,
    validateName,
  };
};
